import { Component } from '@theme/component';

/**
 * @typedef {Object} AxFeatureHighlightsRefs
 * @property {HTMLElement} mediaColumn - Media stack container
 * @property {HTMLElement[]} [items] - Feature list items (hover mode only)
 */

/**
 * ax-feature-highlights — heading + intro + list of feature items with a side
 * media. Two modes:
 *   - 'static': pure CSS, no JS runs (early-return in connectedCallback).
 *   - 'hover': pointerenter / focus / click on an item swaps which media slot
 *     is visible. No item is active by default → main slot stays visible until
 *     the user actually interacts. pointerleave on the list deactivates so the
 *     main slot reappears (handled via :has() in CSS for the dimming).
 *
 * Keyboard nav within the list: Arrow Up/Down + Home/End move focus and
 * activate via the on:focus handler. Tab cycles in/out of the list normally.
 *
 * @extends {Component<AxFeatureHighlightsRefs>}
 */
class AxFeatureHighlightsComponent extends Component {
  #classes = {
    activeItem: 'is-active',
    activeMedia: 'is-active',
  };

  #selectors = {
    itemMedia: '.ax-feature-highlights__media--item',
  };

  /** @type {number} -1 means "no active item, main media visible" */
  #activeIndex = -1;

  /** @type {'hover' | 'static'} */
  #mode = 'static';

  /** @type {HTMLElement[]} */
  #itemMedias = [];

  connectedCallback() {
    super.connectedCallback();
    this.#mode = /** @type {'hover' | 'static'} */ (this.dataset.mode ?? 'static');

    if (this.#mode !== 'hover') return;

    this.#refreshItemMedias();
    this.addEventListener('keydown', this.#handleKeydown);
    document.addEventListener('model:update', this.#onModelUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('model:update', this.#onModelUpdate);
    if (this.#mode !== 'hover') return;
    this.removeEventListener('keydown', this.#handleKeydown);
    this.#itemMedias = [];
  }

  /** Re-cache item medias after connect / model-picker morph (see pdp-morph.md). */
  #onModelUpdate = () => {
    if (this.#mode !== 'hover') return;
    this.#activeIndex = -1;
    this.#refreshItemMedias();
  };

  /** Query live media slots, drop [hidden], clear active media classes. */
  #refreshItemMedias() {
    const column = this.querySelector('.ax-feature-highlights__media-column');
    this.#itemMedias = column
      ? Array.from(column.querySelectorAll(this.#selectors.itemMedia))
      : [];
    for (const media of this.#itemMedias) {
      media.removeAttribute('hidden');
      media.classList.remove(this.#classes.activeMedia);
    }
  }

  /**
   * Event handler invoked via `on:pointerenter / on:focus / on:click` from
   * Liquid. The Component framework passes data first, then the event, when
   * the attribute value contains a `/<data>` suffix.
   * @param {string | number} indexAttr - parsed via parseValue (numeric → number)
   * @param {Event} _event
   */
  handleItemActivate = (indexAttr, _event) => {
    if (this.#mode !== 'hover') return;
    const index = Number(indexAttr);
    if (Number.isNaN(index) || index === this.#activeIndex) return;
    this.#activateItem(index, { revealMedia: true });
  };

  /**
   * Pointer leaves the list (desktop) → revert to "no active item" so the main
   * media reappears. Mobile (no pointer hover) keeps the tap-activated state.
   */
  handleListLeave = () => {
    if (this.#mode !== 'hover') return;
    this.#deactivateAll();
  };

  /**
   * Toggle .is-active on the targeted item + its media. Items that have no
   * matching media (no image/video uploaded) keep the main slot visible.
   * @param {number} index
   * @param {{ revealMedia?: boolean }} [options]
   */
  #activateItem(index, { revealMedia = false } = {}) {
    const items = this.refs.items ?? [];
    if (index < 0 || index >= items.length) return;

    this.#activeIndex = index;

    for (let i = 0; i < items.length; i++) {
      items[i].classList.toggle(this.#classes.activeItem, i === index);
    }

    if (!revealMedia) return;

    const targetMedia = this.#findMediaForIndex(index);

    for (const media of this.#itemMedias) {
      const isTarget = media === targetMedia;
      media.classList.toggle(this.#classes.activeMedia, isTarget);
    }

    if (targetMedia) {
      this.#triggerLazyLoad(targetMedia);
      this.#resumeVideos(targetMedia);
    }
  }

  /**
   * Clear active state on every item + media. The CSS :has() rule then re-shows
   * the main media slot, and dimming reverts because no .is-active remains.
   */
  #deactivateAll() {
    this.#activeIndex = -1;
    for (const item of this.refs.items ?? []) {
      item.classList.remove(this.#classes.activeItem);
    }
    for (const media of this.#itemMedias) {
      media.classList.remove(this.#classes.activeMedia);
    }
  }

  /**
   * @param {number} index
   * @returns {HTMLElement | null}
   */
  #findMediaForIndex(index) {
    for (const media of this.#itemMedias) {
      if (Number(media.dataset.blockIndex) === index) return media;
    }
    return null;
  }

  /**
   * Ask observer-CFH to upgrade any data-lazy-keep media inside the slot. The
   * snippet doesn't emit data-lazy-keep today, but observer auto-loads
   * lazy-src / lazy-source as soon as the slot becomes visible — this dispatch
   * is a safety net if data-lazy-keep is added later for performance.
   * @param {HTMLElement} media
   */
  #triggerLazyLoad(media) {
    document.dispatchEvent(
      new CustomEvent('removeLazyKeep', {
        detail: { lazyParentElement: media },
      })
    );
  }

  /**
   * Restart muted/autoplay videos when their slot becomes visible. Browsers
   * may pause hidden videos to save resources; calling play() is safe because
   * the element is muted + playsinline.
   * @param {HTMLElement} media
   */
  #resumeVideos(media) {
    const videos = media.querySelectorAll('video');
    for (const video of videos) {
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          // Autoplay can fail (e.g. low-power mode) — fail silently.
        });
      }
    }
  }

  /**
   * Arrow Up/Down + Home/End within the feature list. Tab is handled natively.
   * @param {KeyboardEvent} event
   */
  #handleKeydown = (event) => {
    const items = this.refs.items ?? [];
    if (items.length === 0) return;

    const target = /** @type {HTMLElement} */ (event.target);
    const currentIndex = items.indexOf(target);
    if (currentIndex === -1) return;

    let nextIndex = currentIndex;

    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        nextIndex = (currentIndex + 1) % items.length;
        break;
      case 'ArrowUp':
      case 'ArrowLeft':
        nextIndex = (currentIndex - 1 + items.length) % items.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = items.length - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    items[nextIndex]?.focus();
    // Focus dispatch already activates via on:focus; no need to call activate
    // again here — the declarative handler covers it.
  };
}

customElements.define('ax-feature-highlights-component', AxFeatureHighlightsComponent);
